import { CSSObject, alpha } from '@mui/material';

import { COLORS } from '../../../configs/colors';

/** A tag pill's two colours: a pastel fill and the dark ink measured against it. */
export interface TagPalette {
  bg: string;
  text: string;
}

/**
 * The pastel pairs a tag can be drawn in. Every pair is one of the V4 tag tokens
 * in `configs/colors.ts`, where the text value is already measured to clear
 * WCAG AA on its own background — which is the reason tags are mapped onto a
 * fixed set rather than tinted from their stored colour. A stored colour is
 * arbitrary (legacy rows hold saturated primaries, and `'red'`/`'blue'` names),
 * so deriving a fill from it can land anywhere on the contrast range.
 */
const TAG_PALETTES: TagPalette[] = [
  { bg: COLORS.TagBlueBg, text: COLORS.TagBlueText },
  { bg: COLORS.TagGreenBg, text: COLORS.TagGreenText },
  { bg: COLORS.TagAmberBg, text: COLORS.TagAmberText },
  { bg: COLORS.TagPurpleBg, text: COLORS.TagPurpleText },
  { bg: COLORS.TagRedBg, text: COLORS.TagRedText },
  { bg: COLORS.TagIndigoBg, text: COLORS.TagIndigoText },
];

/** Slate — the neutral a tag falls back to when it has no colour at all. */
const NEUTRAL_PALETTE: TagPalette = {
  bg: COLORS.StatusDefaultBg,
  text: COLORS.StatusDefaultText,
};

/**
 * Stored colour → pastel pair, by hue, for the `defaultColors` palette a tag is
 * actually created with. Slate maps to the neutral pair; the remaining eight
 * collapse onto the six pastels, so a few hues share a pair. That is deliberate:
 * a tag is identified by its label, and the colour only groups it loosely.
 */
const HUE_MAP: Record<string, TagPalette> = {
  '#334155': NEUTRAL_PALETTE, // slate
  '#0f766e': TAG_PALETTES[1], // teal   → green
  '#1d4ed8': TAG_PALETTES[0], // indigo → blue
  '#6d28d9': TAG_PALETTES[3], // violet → purple
  '#a16207': TAG_PALETTES[2], // amber
  '#b91c1c': TAG_PALETTES[4], // clay   → red
  '#15803d': TAG_PALETTES[1], // moss   → green
  '#0e7490': TAG_PALETTES[5], // steel  → indigo
  '#9d174d': TAG_PALETTES[4], // plum   → red
};

/** Stable index for a colour outside `defaultColors`, so legacy rows keep a
 *  consistent pastel across renders and sessions instead of flickering or all
 *  collapsing to grey. Sum of char codes is enough — it only has to be
 *  deterministic, not well-distributed. */
const hashToPalette = (value: string): TagPalette => {
  const sum = [...value].reduce((total, char) => total + char.charCodeAt(0), 0);
  return TAG_PALETTES[sum % TAG_PALETTES.length];
};

/**
 * Resolve a tag's stored colour to the pastel pair it renders in.
 *
 * Exported because the pill's hover and delete-icon styling live in
 * `Tags.styles.ts`, and those have to reach the same answer — while they read
 * the raw stored colour, hovering an editable tag snapped it back to its old
 * saturated fill.
 */
export const resolveTagPalette = (colour?: string): TagPalette => {
  // Trim before the emptiness check, not after: a whitespace-only colour is
  // just as absent as an empty one, and hashing it would pick an arbitrary
  // palette rather than the neutral fallback.
  const key = colour?.trim().toLowerCase();
  if (!key) return NEUTRAL_PALETTE;
  return HUE_MAP[key] ?? hashToPalette(key);
};

/**
 * The single definition of how a tag pill looks.
 *
 * Both the editable Tags field and the read-only pills in the detail header use
 * this, so the same tag cannot appear in two different treatments — which it did
 * while each place carried its own copy of the styling.
 *
 * A tag is drawn as a soft pastel fill with dark ink on top, rather than in its
 * own stored colour. The stored colours are saturated enough that a row of tags
 * read as a set of alerts, and using one as chip text put legibility at the
 * mercy of whichever colour someone had picked.
 *
 * System tags keep a visible distinction — a faint border in their own ink —
 * because that separation carried meaning before and still does.
 */
export const tagChipStyles = (colour?: string, isSystem?: boolean): CSSObject => {
  const { bg, text } = resolveTagPalette(colour);
  return {
    height: 22,
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: text,
    backgroundColor: bg,
    border: isSystem ? `1px solid ${alpha(text, 0.32)}` : '1px solid transparent',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  };
};
